import {
  reactive,
  computed,
  watch,
  toRefs,
  ref,
  toRef,
  MaybeRefOrGetter,
} from 'vue';
import {
  useQuery,
  useQueryCache,
  useInfiniteQuery,
} from '@zoomcatalog/query-client';
import { isAxiosError, AxiosError } from 'axios';
import api from '@/config/axios';
import { IDesign, ISimplePage, IPage } from '../ts-definitions';

export default function useQuickCanvasService() {
  const getPublishedDesign = (designId: string) =>
    api.get<IDesign>(`project/${designId}/publish`);

  const getSinglePage = (designId: string, pageNumber: number) =>
    api.get<ISimplePage[]>(`project/${designId}/pages`, {
      params: { index: pageNumber },
    });

  const getPublishedPage = (designId: string, pageId: string) =>
    api.get<IPage>(`project/${designId}/page/${pageId}/publish`);

  const getRawPagesById = (designId: string, pageIds: Array<string>) =>
    api.get<ISimplePage[]>(`project/${designId}/raw-pages`, {
      params: { index: pageIds.join(',') },
    });

  return {
    getPublishedDesign,
    getRawPagesById,
    getPublishedPage,
    getSinglePage,
  };
}

export type UsePublishedDesignProps = {
  projectId: string;
  enabled: boolean;
};

export function usePublishedDesign(props: UsePublishedDesignProps) {
  return useQuery(
    reactive(['project', 'published', props.projectId]),
    reactive({
      async queryFn() {
        return api
          .get<IDesign>(`project/${props.projectId}/publish`)
          .then((data) => data.data);
      },
      staleTime: Infinity,
      refetchOnMount: false,
      enabled: computed(() => Boolean(props.enabled)),
    })
  );
}

export function useInvalidatePublishedDesignQuery() {
  const cache = useQueryCache();
  return async () => cache.invalidateQueries(['project', 'published']);
}

export type UseDraftDesignProps = {
  projectId: string;
  enabled: boolean;
};

export function useDraftDesignQuery(props: UseDraftDesignProps) {
  return useQuery<IDesign, { code?: number }>(
    reactive(['project', 'draft', props.projectId]),
    reactive({
      async queryFn() {
        return api
          .get<IDesign>(`project/${props.projectId}`)
          .then((data) => data.data);
      },
      staleTime: Infinity,
      refetchOnMount: false,
      enabled: computed(() => Boolean(props.enabled)),
    })
  );
}

export type UsePagesDesignProps = {
  projectId: string;
  enabled?: boolean;
  limit: number;
  totalPages?: number;
};

export type UsePagesDesignHandlers = {
  onSuccess?: (pages: Array<IPage>) => void;
  onSuccessPage?: (pages: IPage) => void;
};

export function useGetPages(props: UsePagesDesignProps) {
  const { projectId, enabled, limit, totalPages } = toRefs(props);

  return useInfiniteQuery<Array<IPage>, AxiosError>(
    reactive([
      'pages',
      {
        projectId,
        limit,
        totalPages,
      },
    ]),
    reactive({
      queryFn: async (
        _key: string,
        data: {
          projectId: string;
          index: number[];
          limit: number;
        },
        offset = 0
      ) => {
        const pagesIndex = computed(() =>
          Array.from(Array(data.limit), (_, idx) => idx + (offset || 1))
        );
        return api
          .get<Array<IPage>>(`project/${data.projectId}/pages`, {
            params: { index: pagesIndex?.value.join(',') },
          })
          .then((data) => data.data);
      },
      getFetchMore: (lastPage, allPages) => {
        if (lastPage.length < limit.value) {
          return false;
        }
        return (
          allPages.reduce(
            (accumulator, pages) => accumulator + pages.length,
            0
          ) + 1
        );
      },
      staleTime: Infinity,
      refetchOnMount: false,
      enabled: computed(
        () => projectId.value && enabled?.value && Boolean(totalPages?.value)
      ),
    })
  );
}

export type UseCheckDesignLockStatusProps = {
  projectId: string;
};

export function useCheckDesignLockStatus(props: UseCheckDesignLockStatusProps) {
  const isDesignLocked = ref(false);

  const designLockStatusQuery = useQuery(
    reactive(['project', 'locked', props.projectId]),
    {
      async queryFn() {
        return api.head(`project/${props.projectId}`).then((data) => data.data);
      },
      staleTime: Infinity,
      retry: (_failureCount, error) => {
        const isLocked = isAxiosError(error) && error.response?.status === 423;
        isDesignLocked.value = isLocked;

        return isLocked;
      },
      retryDelay: 2_500,
    }
  );

  watch(designLockStatusQuery.isSuccess, () => {
    isDesignLocked.value = false;
  });

  return { designLockStatusQuery, isDesignLocked };
}

export type UsePageDesignProps = {
  projectId: MaybeRefOrGetter<string>;
  pageId: MaybeRefOrGetter<string | undefined>;
};

export function useGetPage(props: UsePageDesignProps) {
  const { projectId, pageId } = toRefs(props);

  return useQuery<IPage, { code?: number }>(
    reactive(['page', { projectId, pageId }]),
    reactive({
      async queryFn(_key, params: { projectId: string; pageId: string }) {
        return api
          .get<IPage>(
            `project/${params.projectId}/page/${params.pageId}/publish`
          )
          .then((data) => data.data);
      },
      staleTime: Infinity,
      refetchOnMount: false,
      enabled: toRef(() => Boolean(props.projectId && props.pageId)),
    })
  );
}

export function useInvalidatePageQuery() {
  const cache = useQueryCache();
  return async (args?: Parameters<typeof cache.invalidateQueries>[0]) =>
    cache.invalidateQueries(args ?? ['page']);
}

export type UseGetProjectProps = {
  projectId: string;
};

export type UseGetProjectHandlers = {
  onSuccessDesignData?: (data: IDesign) => void;
};

export function useGetDesign(
  props: UseGetProjectProps,
  handlers: UseGetProjectHandlers = {}
) {
  const { projectId } = toRefs(props);
  const { designLockStatusQuery, isDesignLocked } =
    useCheckDesignLockStatus(props);
  const designStatusRef = ref<'private' | 'locked' | null>(null);

  const designStatus = toRef(() => {
    if (isDesignLocked.value) return 'locked';

    return designStatusRef.value;
  });

  const designQuery = usePublishedDesign(
    reactive({
      projectId,
      enabled: toRef(
        () => !isDesignLocked.value && !designLockStatusQuery.isLoading.value
      ),
    })
  );

  watch(
    () => designQuery.data.value,
    (data) => {
      if (!data) return;
      designStatusRef.value = null;
      handlers.onSuccessDesignData?.(data);
    }
  );

  watch(designQuery.error, (nextError) => {
    if (!nextError || !isAxiosError(nextError)) return;
    const isLocked = nextError?.response?.status === 423;
    const isPrivate = nextError?.response?.status === 403;

    if (isLocked) designStatusRef.value = 'locked';
    if (isPrivate) designStatusRef.value = 'private';
  });

  return [designQuery, designLockStatusQuery, designStatus] as const;
}

export function useGetPagesByIdProject(
  props: UsePagesDesignProps,
  handlers: UsePagesDesignHandlers = {}
) {
  const { projectId, enabled, limit, totalPages } = toRefs(props);

  const pagesQueryOptions: UsePagesDesignProps = reactive({
    projectId,
    enabled: enabled ?? true,
    limit,
    totalPages,
  });

  const pagesQuery = useGetPages(pagesQueryOptions);

  watch(
    () => pagesQuery.updatedAt?.value,
    () => {
      const pages = pagesQuery.data.value;
      if (pages?.[pages?.length - 1]) {
        handlers.onSuccess?.(pages?.[pages?.length - 1] as IPage[]);
      }
    }
  );

  return pagesQuery;
}

export function useGetPageByIdProject(
  props: UsePageDesignProps,
  handlers: UsePagesDesignHandlers = {}
) {
  const pageQuery = useGetPage(props);

  watch(
    () => pageQuery.updatedAt?.value,
    () => {
      const page = pageQuery.data.value;
      if (page) handlers.onSuccessPage?.(page);
    }
  );

  return pageQuery;
}
